import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from '@mui/material';
import {
  Groups as GroupsIcon,
  School as SchoolIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { adminFetch } from '../utils/adminFetch';
import { getAdminCourseName } from '../utils/courseDisplay';

interface TrackingGroup {
  id: string;
  name: string;
  studentCount: number;
}

interface TrackingCourse {
  courseId: string;
  courseProject?: string;
  title: { [key: string]: string } | string;
  defaultLanguage?: string;
  groups?: TrackingGroup[];
  studentCount: number;
}

interface TrackingOverview {
  role: 'coach' | 'entreprise';
  company?: { id: string; name: string; slug: string };
  courses: TrackingCourse[];
}

const StudentTracking: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<TrackingOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadOverview = async () => {
      if (!user || user.role === 'admin') {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('admin_token');
        const response = await adminFetch(
          `${process.env.REACT_APP_API_URL}/tracking/overview`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!response.ok) {
          throw new Error('Impossible de charger le suivi apprenant');
        }

        setData(await response.json());
      } catch (err: any) {
        setError(err?.message || 'Erreur lors du chargement');
      } finally {
        setLoading(false);
      }
    };

    void loadOverview();
  }, [user]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box mb={3}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Suivi apprenants
        </Typography>
        <Typography color="text.secondary">
          {user?.role === 'coach'
            ? 'Formations et groupes auxquels vous êtes assigné comme coach.'
            : data?.company
            ? `Apprenants de l'entreprise ${data.company.name}.`
            : 'Consultez la progression des apprenants de votre entreprise.'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!error && data?.courses.length === 0 && (
        <Alert severity="info">
          {user?.role === 'coach'
            ? 'Aucun groupe ne vous est assigné pour le moment.'
            : 'Aucun apprenant de votre entreprise n’est inscrit à une formation.'}
        </Alert>
      )}

      <Stack spacing={2}>
        {(data?.courses || []).map((course) => (
          <Card key={course.courseId}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    {getAdminCourseName({
                      title: course.title,
                      courseProject: course.courseProject,
                      defaultLanguage: course.defaultLanguage,
                    })}
                  </Typography>
                  <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" useFlexGap>
                    <Chip
                      icon={<SchoolIcon />}
                      label={`${course.studentCount} apprenant${course.studentCount > 1 ? 's' : ''}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                    {course.groups
                      ?.filter((group) => group.studentCount > 0)
                      .map((group) => (
                      <Chip
                        key={group.id}
                        icon={<GroupsIcon />}
                        label={`${group.name} (${group.studentCount})`}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<VisibilityIcon />}
                  onClick={() => navigate(`/courses/${course.courseId}/students`)}
                >
                  Voir les apprenants
                </Button>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Container>
  );
};

export default StudentTracking;
